import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { type PointsView, setPointsView } from '../../../../store/reducers/measurementPlanes.reducer.ts';

/**
 * Floating switch (above the measurement legend) that chooses which points the scene
 * shows — the whole-model measurement OR the drawn-surface points. Never both at once.
 * Lives outside the sidebar so the choice is obvious next to the colour scale.
 */
export function MeasurementPointsSwitch(): JSX.Element | null {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const planeCount = useAppSelector((s) => s.measurementPlanes.planes.length);
  const pointsView = useAppSelector((s) => s.measurementPlanes.pointsView);

  if (planeCount === 0) return null;

  const seg = (view: PointsView, label: string): JSX.Element => {
    const active = pointsView === view;
    return (
      <button
        onClick={() => dispatch(setPointsView(view))}
        style={{
          padding: '5px 12px',
          fontSize: '12px',
          fontWeight: active ? 600 : 500,
          color: active ? '#0a0a0a' : 'rgba(255,255,255,0.78)',
          background: active ? '#B3EC49' : 'transparent',
          border: 'none',
          borderRadius: '7px',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          transition: 'background 0.12s, color 0.12s',
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '218px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '5px 8px 5px 12px',
        background: 'rgba(18, 18, 20, 0.78)',
        backdropFilter: 'blur(18px) saturate(160%)',
        WebkitBackdropFilter: 'blur(18px) saturate(160%)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.09)',
        boxShadow: '0 12px 36px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.06)',
        zIndex: 50,
        pointerEvents: 'auto',
        userSelect: 'none',
      }}
    >
      <span
        style={{
          fontSize: '11px',
          color: 'rgba(255,255,255,0.55)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {t('workspace.panel.surfaces.pointsShowing')}
      </span>
      <div
        style={{
          display: 'flex',
          gap: '4px',
          background: 'rgba(255,255,255,0.06)',
          padding: '3px',
          borderRadius: '9px',
        }}
      >
        {seg('model', t('workspace.panel.surfaces.modelPoints'))}
        {seg('surface', t('workspace.panel.surfaces.areaPoints'))}
      </div>
    </div>
  );
}
